import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { apiGet, apiPost } from '../Api';
import './Auth.css';
import './Register.css';
import PublicShell from './PublicShell';
import {
    getPasswordChecks,
    hasErrors,
    mapRegistrationError,
    validateEmail,
    validatePassword,
    validateRegistration,
    validateUsername,
} from './authValidation';

const fieldValidators = {
    username: validateUsername,
    email: validateEmail,
    password: validatePassword,
};

export default function Register() {
    const [values, setValues] = useState({
        username: '',
        email: '',
        password: '',
        newsletterConsent: false,
    });
    const [errors, setErrors] = useState({
        username: '',
        email: '',
        password: '',
        newsletterConsent: '',
    });
    const [status, setStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registeredSuccessfully, setRegisteredSuccessfully] = useState(false);
    const [registrationOpen, setRegistrationOpen] = useState(true);
    const [registrationStatusLoading, setRegistrationStatusLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const fieldRefs = useRef({});
    const passwordChecks = getPasswordChecks(values.password);

    useEffect(() => {
        let active = true;

        async function loadRegistrationStatus() {
            try {
                const response = await apiGet('/user/registration-status', false);
                const result = await response.json();
                if (active) setRegistrationOpen(result.registrationOpen);
            } catch {
                // The registration endpoint remains the final authority.
            } finally {
                if (active) setRegistrationStatusLoading(false);
            }
        }

        loadRegistrationStatus();
        return () => { active = false; };
    }, []);

    const focusFirstError = (nextErrors) => {
        const field = ['username', 'email', 'password', 'newsletterConsent']
            .find((name) => nextErrors[name]);
        if (field) fieldRefs.current[field]?.focus();
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues((current) => ({ ...current, [name]: value }));
        setStatus(null);

        if (errors[name]) {
            setErrors((current) => ({
                ...current,
                [name]: fieldValidators[name](value),
            }));
        }
    };

    const handleBlur = (event) => {
        const { name, value } = event.target;
        setErrors((current) => ({
            ...current,
            [name]: fieldValidators[name](value),
        }));
    };

    const handleNewsletterConsent = (event) => {
        const newsletterConsent = event.target.checked;
        setValues((current) => ({ ...current, newsletterConsent }));
        setErrors((current) => ({
            ...current,
            newsletterConsent: newsletterConsent
                ? ''
                : 'Confirm that you agree to receive the newsletter.',
        }));
        setStatus(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const nextErrors = validateRegistration(values);
        setErrors(nextErrors);
        setStatus(null);
        if (hasErrors(nextErrors)) {
            focusFirstError(nextErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new URLSearchParams();
            formData.append('username', values.username.trim());
            formData.append('password', values.password);
            formData.append('email', values.email.trim());
            formData.append('newsletterConsent', String(values.newsletterConsent));

            const response = await apiPost('/user/register', formData, false);
            const result = await response.json();

            if (!result.success) {
                const mappedError = mapRegistrationError(result.message);
                const { form, ...fieldErrors } = mappedError;
                setErrors((current) => ({ ...current, ...fieldErrors }));
                setStatus(form ? { type: 'error', text: form } : null);
                focusFirstError(fieldErrors);
                return;
            }

            setRegisteredSuccessfully(true);
        } catch {
            setStatus({
                type: 'error',
                text: 'We could not create your account. Your entries are still here—please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    let cardContent;
    if (registrationStatusLoading) {
        cardContent = (
            <div aria-live="polite" aria-busy="true">
                <p className="auth-card__eyebrow">Checking the gate</p>
                <h1 id="register-title">Pre-register</h1>
                <p className="auth-card__intro">Loading pre-registration status…</p>
            </div>
        );
    } else if (!registrationOpen) {
        cardContent = (
            <div className="auth-success registrationClosed" role="status">
                <div className="auth-success__marker" aria-hidden="true">!</div>
                <h1 id="register-title">Pre-registration paused</h1>
                <p>New pre-registrations are paused right now. Existing operatives can still log in.</p>
                <Link className="auth-button auth-button--secondary" to="/login">Back to login</Link>
            </div>
        );
    } else if (registeredSuccessfully) {
        cardContent = (
            <div className="auth-success" role="status">
                <div className="auth-success__marker" aria-hidden="true">✓</div>
                <p className="auth-card__eyebrow">Pre-registration received</p>
                <h1 id="register-title">Check your inbox</h1>
                <p>
                    We sent a confirmation link to <strong>{values.email.trim()}</strong>.
                    Check your spam folder too—the wasteland is not known for reliable mail.
                </p>
                <Link className="auth-button" to="/login">Go to login</Link>
            </div>
        );
    } else {
        cardContent = (
            <>
                <p className="auth-card__eyebrow">Closed alpha sign-up</p>
                <h1 id="register-title">Pre-register</h1>
                <p className="auth-card__intro">
                    Reserve your operative account before release. Pre-registration includes
                    the Progress Lands development newsletter and requires email confirmation.
                </p>

                <form className="auth-form" onSubmit={handleSubmit} noValidate>
                    {status && (
                        <p className="auth-status auth-status--error" role="alert">
                            {status.text}
                        </p>
                    )}

                    <div className="auth-field">
                        <label htmlFor="register-username">Username</label>
                        <input
                            ref={(element) => { fieldRefs.current.username = element; }}
                            id="register-username"
                            name="username"
                            type="text"
                            value={values.username}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck="false"
                            aria-invalid={Boolean(errors.username)}
                            aria-describedby={errors.username ? 'register-username-hint register-username-error' : 'register-username-hint'}
                        />
                        <p id="register-username-hint" className="auth-field__hint">
                            At least 3 characters. This is the name you use to log in.
                        </p>
                        {errors.username && (
                            <p id="register-username-error" className="auth-field__error">
                                {errors.username}
                            </p>
                        )}
                    </div>

                    <div className="auth-field">
                        <label htmlFor="register-email">Email address</label>
                        <input
                            ref={(element) => { fieldRefs.current.email = element; }}
                            id="register-email"
                            name="email"
                            type="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            autoComplete="email"
                            autoCapitalize="none"
                            spellCheck="false"
                            inputMode="email"
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={errors.email ? 'register-email-hint register-email-error' : 'register-email-hint'}
                        />
                        <p id="register-email-hint" className="auth-field__hint">
                            Used for account emails and the newsletter you consent to below.
                        </p>
                        {errors.email && (
                            <p id="register-email-error" className="auth-field__error">
                                {errors.email}
                            </p>
                        )}
                    </div>

                    <div className="auth-field">
                        <label htmlFor="register-password">Password</label>
                        <div className="auth-field__input-wrap">
                            <input
                                ref={(element) => { fieldRefs.current.password = element; }}
                                id="register-password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                autoComplete="new-password"
                                aria-invalid={Boolean(errors.password)}
                                aria-describedby={errors.password ? 'register-password-error register-password-rules' : 'register-password-rules'}
                            />
                            <button
                                className="auth-password-toggle"
                                type="button"
                                aria-pressed={showPassword}
                                onClick={() => setShowPassword((current) => !current)}
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        <ul id="register-password-rules" className="auth-password-rules">
                            <li data-met={passwordChecks.hasMinimumLength}>
                                6 or more characters
                            </li>
                            <li data-met={passwordChecks.hasEnoughVariation}>
                                Any 2 types: lower, upper, number, symbol
                            </li>
                        </ul>
                        {errors.password && (
                            <p id="register-password-error" className="auth-field__error">
                                {errors.password}
                            </p>
                        )}
                    </div>

                    <div className="auth-consent">
                        <input
                            ref={(element) => { fieldRefs.current.newsletterConsent = element; }}
                            id="register-newsletter-consent"
                            name="newsletterConsent"
                            type="checkbox"
                            checked={values.newsletterConsent}
                            onChange={handleNewsletterConsent}
                            aria-invalid={Boolean(errors.newsletterConsent)}
                            aria-describedby={
                                errors.newsletterConsent
                                    ? 'register-newsletter-hint register-newsletter-error'
                                    : 'register-newsletter-hint'
                            }
                        />
                        <div>
                            <label htmlFor="register-newsletter-consent">
                                I agree to receive pre-release news and the Progress Lands
                                newsletter by email.
                            </label>
                            <p id="register-newsletter-hint" className="auth-field__hint">
                                Required for pre-registration. You can unsubscribe from future
                                emails at any time.
                            </p>
                            {errors.newsletterConsent && (
                                <p id="register-newsletter-error" className="auth-field__error">
                                    {errors.newsletterConsent}
                                </p>
                            )}
                        </div>
                    </div>

                    <button
                        className="auth-button"
                        type="submit"
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                    >
                        {isSubmitting ? 'Pre-registering…' : 'Pre-register'}
                    </button>
                </form>

                <p className="auth-card__alternate">
                    Already have an account? <Link to="/login">Log in</Link>
                </p>
            </>
        );
    }

    return (
        <PublicShell compact>
            <section className="auth-card" aria-labelledby="register-title">
                {cardContent}
            </section>
        </PublicShell>
    );
}
